import { EventDoc } from "../types/event";

/**
 * Who entered an event and by what means.
 *
 * Lifted out of EventDetailsTab when the line moved to the sidebar. It is provenance
 * rather than a field — read-only, and answering a question ABOUT the event rather
 * than being part of it — which is why it now sits with the other things the sidebar
 * reports rather than at the end of the form.
 */

/** A stored timestamp as a plain date, e.g. "14 August 2026". */
function stampedOn(iso: string | null | undefined): string {
	if (!iso) return "";
	const d = new Date(iso);
	if (Number.isNaN(d.getTime())) return "";
	return d.toLocaleDateString("en-AU", {
		day: "numeric",
		month: "long",
		year: "numeric",
	});
}

/**
 * HOW the event got here, which is what `ForeignKeys` records: Edumate and
 * Clipboard each stamp their own key, and the create path in this app
 * deliberately writes none.
 *
 * The same distinction the filter rail calls Event source, and deliberately the
 * same evidence — `Category` is a label the API computes afterwards, so reading
 * that here would let the two disagree about one event.
 */
function methodOf(event: EventDoc): "Edumate" | "Clipboard" | "Event Manager" {
	const keys = event.ForeignKeys ?? {};
	if (keys.Edumate) return "Edumate";
	if (keys.Clipboard) return "Clipboard";
	return "Event Manager";
}

/**
 * Who entered this event and by what means, in one line.
 *
 * The method is stated even when the person is known. "Entered by Jane Smith"
 * alone leaves the more useful half of the answer out: whether an event can be
 * edited here at all depends on where it came from — a synced one has its
 * fields overwritten by the next sync — and that is the question someone
 * reading this line is usually asking.
 *
 * `CreatedBy` is only written by the create path in this app, so it is absent
 * from the overwhelming majority of events — 86,225 in the collection, and a
 * bare handful carry it. A blank line on almost every event would be worse than
 * no line, so where there is no person the method answers on its own.
 *
 * A stored name of "Unknown" is treated as no name. The API resolves the actor's
 * name at write time and, before that lookup was fixed, wrote that literal
 * string — so it is a record of a failed lookup, not of a person, and repeating
 * it back reads as an assertion about who did it.
 */
export function originOf(event: EventDoc): string {
	const on = stampedOn(event.CreatedAt);
	const suffix = on ? ` on ${on}` : "";
	const method = methodOf(event);

	const name = (event.CreatedBy?.Name ?? "").trim();
	if (name && name.toLowerCase() !== "unknown") {
		return `Entered by ${name} in ${method}${suffix}`;
	}

	// Imported, and by nobody in particular: a sync is not a person, so it is
	// named as the act it was rather than given "by" and a blank.
	if (method !== "Event Manager") return `Imported from ${method}${suffix}`;

	// Made here, we just do not know by whom — every event created before the
	// API started recording it.
	return on ? `Entered in Event Manager on ${on}` : "";
}
